import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { isRender } from "../catalog";
import CompareButton from "./CompareButton";

/**
 * Reusable Apple-style tile: image on top, title + subtitle centered on the image or below.
 * Variants:
 *   - "cover"  → image is background, text overlaid (used for macro tiles on Products page)
 *   - "card"   → standard card with image + text below (used for sub-cat and product tiles)
 */
export default function Tile({ to, image, tag, title, subtitle, variant = "card", testId, dark = false, index = 0, pills = null, fit, compareSlug }) {
  const contain = fit ? fit === "contain" : isRender(image);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-10% 0px" });

  if (variant === "cover") {
    return (
      <motion.div
        ref={ref}
        initial={{ opacity: 0, y: 30 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.9, delay: index * 0.1, ease: [0.19, 1, 0.22, 1] }}
        className="card-tile relative"
        data-testid={testId}
      >
        <Link to={to} className="block relative overflow-hidden rounded-3xl">
          <div className="relative h-[400px] md:h-[540px] w-full overflow-hidden bg-[#0b0f1a]">
            <img src={image} alt="" className="w-full h-full object-cover opacity-90" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          </div>
          <div className="absolute inset-x-0 top-10 md:top-16 text-center px-6">
            {tag && <div className="text-[12px] uppercase tracking-widest text-white/70 mb-3">{tag}</div>}
            <h3 className="text-[28px] md:text-[40px] font-semibold tracking-[-0.02em] text-white leading-tight">
              {title}
            </h3>
            {subtitle && (
              <p className="mt-3 text-[15px] md:text-[17px] text-white/85 max-w-md mx-auto">
                {subtitle}
              </p>
            )}
            <div className="mt-5 inline-flex items-center gap-1 text-white text-[14px] a-link !text-white">
              <span>{"Scopri"}</span> <span>›</span>
            </div>
          </div>
        </Link>
      </motion.div>
    );
  }

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay: index * 0.08, ease: [0.19, 1, 0.22, 1] }}
      className="card-tile relative w-full h-full"
      data-testid={testId}
    >
      <div className={`h-full flex flex-col ${dark ? "bg-[#0b0f1a] text-white" : "bg-white text-[#1d1d1f]"} rounded-3xl overflow-hidden border border-[#e4e4e7]`}>
      <Link to={to} className="block flex-1">
        <div className={`relative w-full aspect-[4/3] overflow-hidden ${contain ? "bg-white" : ""}`}>
          <img src={image} alt="" className={contain ? "w-full h-full object-contain object-center p-5 md:p-6" : "w-full h-full object-cover"} loading="lazy" />
        </div>
        <div className="p-6 md:p-7">
          {tag && (
            <div className={`text-[12px] uppercase tracking-widest mb-2 ${dark ? "text-white/60" : "text-[#6e6e73]"}`}>
              {tag}
            </div>
          )}
          <h3 className={`text-[22px] md:text-[26px] font-semibold tracking-[-0.02em] leading-tight ${dark ? "text-white" : "text-[#1d1d1f]"}`}>
            {title}
          </h3>
          {subtitle && (
            <p className={`mt-2 text-[14px] leading-snug ${dark ? "text-white/75" : "text-[#1d1d1f]/75"}`}>
              {subtitle}
            </p>
          )}
          {pills && pills.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-1.5" data-testid={testId ? `${testId}__pills` : undefined}>
              {pills.map((p, idx) => (
                <span
                  key={idx}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-medium tracking-tight ${
                    p.highlight
                      ? "bg-[#1D3F94] text-white"
                      : dark
                      ? "bg-white/10 text-white/90"
                      : "bg-[#f5f5f7] text-[#1d1d1f]/80"
                  }`}
                >
                  {p.label}
                </span>
              ))}
            </div>
          )}
          <div className="mt-4 a-link">
            Scopri <span className="chev">›</span>
          </div>
        </div>
      </Link>
      {compareSlug && (
        <div className="px-6 md:px-7 py-3.5 border-t border-[#e4e4e7] flex items-center justify-end" data-testid={testId ? `${testId}__compare` : undefined}>
          <CompareButton slug={compareSlug} variant="inline" />
        </div>
      )}
      </div>
    </motion.div>
  );
}
